/**
 * System Data > Power of Attorney Roles, logic only (both looks).
 *
 * The "Which roles" list the Power of Attorney pop-up offers. HR admins add,
 * rename and switch roles off; a role is never deleted, so the powers of
 * attorney that name it keep it.
 */
import { useCallback, useEffect, useState } from 'react';
import powerOfAttorneyService from '../../services/powerOfAttorneyService';
import { useFlashMessage } from '../FlashMessage';
import { getErrorMessage } from '../../utils/errorMessages';

export default function usePowerOfAttorneyRoles() {
  const { showSuccess, showError } = useFlashMessage();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // The add/rename pop-up: null (closed), 'new', or the role being renamed
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      setRoles(await powerOfAttorneyService.listRoles());
    } catch (err) {
      setLoadError(getErrorMessage(err, "Couldn't load the Power of Attorney roles. Please try again."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing('new');
    setName('');
    setFormError('');
  };

  const openRename = (role) => {
    setEditing(role);
    setName(role.name);
    setFormError('');
  };

  const closeForm = () => {
    if (saving) return;
    setEditing(null);
  };

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setFormError('Enter a name for the role.');
      return;
    }
    setSaving(true);
    try {
      if (editing === 'new') {
        await powerOfAttorneyService.createRole(trimmed);
        showSuccess('Power of Attorney role added');
      } else {
        await powerOfAttorneyService.updateRole(editing.id, { name: trimmed });
        showSuccess('Power of Attorney role renamed');
      }
      setEditing(null);
      await load();
    } catch (err) {
      setFormError(getErrorMessage(err, "Couldn't save the role. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  const setActive = async (role, isActive) => {
    try {
      await powerOfAttorneyService.updateRole(role.id, { is_active: isActive });
      showSuccess(isActive ? `"${role.name}" switched back on` : `"${role.name}" switched off`);
      await load();
    } catch (err) {
      showError(getErrorMessage(err, "Couldn't change the role. Please try again."));
    }
  };

  return {
    roles,
    loading,
    loadError,
    reload: load,
    editing,
    name,
    setName: (value) => {
      setName(value);
      setFormError('');
    },
    formError,
    saving,
    openNew,
    openRename,
    closeForm,
    save,
    setActive,
  };
}
